// src/analytics/tracker.js
import { hasAnalyticsConsent } from "../../common/consent";
import {
  getOrCreateVisitorId,
  getOrCreateSessionId,
  touchSession,
  getOrCreateAttribution,
  getLandingAttribution,
} from "./ids";

const ENDPOINT = "https://www.teardrophydro.co.uk/garageFarm/_stats"; // adjust if your API base differs
let enabled = false;
let initialized = false;

const queue = [];
let flushTimer = null;

function nowIso() {
  return new Date().toISOString();
}

async function flush() {
  flushTimer = null;
  if (!queue.length) return;

  const batch = queue.splice(0, queue.length);

  try {
    await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // Batch format your server endpoint expects
      body: JSON.stringify({
        site: window.location.host,
        events: batch,
      }),
      // Not strictly required, but fine to keep
      credentials: "include",
    });
  } catch (e) {
    // If offline / server down, re-queue a small amount
    for (const ev of batch.slice(-20)) queue.push(ev);
  }
}

function enqueue(ev) {
  queue.push(ev);
  if (!flushTimer) flushTimer = setTimeout(flush, 800);
}

export function setAnalyticsEnabled(on) {
  enabled = !!on;
  if (enabled && !initialized) initActivityTouch();
}

function initActivityTouch() {
  initialized = true;
  const touch = () => touchSession();
  window.addEventListener("click", touch, { passive: true });
  window.addEventListener("keydown", touch, { passive: true });
  window.addEventListener("scroll", touch, { passive: true });
  window.addEventListener("mousemove", touch, { passive: true });

  initOutboundLinkTracking();
  initErrorTracking();
}

// Generic capture for links to other domains, mailto:, and tel: — on top of
// (not instead of) any specific cta_click events already instrumented at
// individual call sites.
function initOutboundLinkTracking() {
  document.addEventListener("click", (e) => {
    const link = e.target.closest && e.target.closest("a[href]");
    if (!link) return;

    const href = link.getAttribute("href") || "";
    let kind = null;
    if (href.startsWith("mailto:")) {
      kind = "mailto";
    } else if (href.startsWith("tel:")) {
      kind = "tel";
    } else {
      try {
        const url = new URL(href, window.location.href);
        if (url.hostname && url.hostname !== window.location.hostname) kind = "external";
      } catch {
        // relative or unparseable href — not outbound
      }
    }
    if (!kind) return;

    track("outbound_link_click", { href, kind, text: (link.textContent || "").trim().slice(0, 80) });
  }, { passive: true });
}

function initErrorTracking() {
  window.addEventListener("error", (e) => {
    track("js_error", {
      message: e.message || null,
      source: e.filename || null,
      line: e.lineno || null,
      col: e.colno || null,
      stack: e.error?.stack ? String(e.error.stack).slice(0, 500) : null,
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    track("js_unhandled_rejection", {
      message: reason?.message || String(reason).slice(0, 300),
      stack: reason?.stack ? String(reason.stack).slice(0, 500) : null,
    });
  });
}

export function track(event, props = {}, force = false) {
  // if (!enabled && !force) return;

  const vid = getOrCreateVisitorId();
  const sid = getOrCreateSessionId();
  // Persisted first-touch attribution once consented; raw/ephemeral landing
  // info (nothing stored) on the anonymous forced pre-consent event only.
  const attribution = enabled ? getOrCreateAttribution() : (force ? getLandingAttribution() : null);

  const payload = {
    ts: nowIso(),
    event,
    vid,
    sid,
    path: window.location.pathname + window.location.search,
    ref: document.referrer || null,
    title: document.title || null,
    attribution,
    props, // keep custom fields here
  };

  enqueue(payload);
}

export function initAnalytics() {
  const hasConsent = hasAnalyticsConsent();
  setAnalyticsEnabled(hasConsent);

  // If they haven't consented yet, send a 'forced' anonymous hit 
  // so you can see the visit in your dashboard.
  if (!hasConsent) {
    track("initial_landing", { type: "anonymous_count" }, true);
  }
}